import { ReportSection } from 'features/reporting/types'
import { SectionAddendumsEditor } from 'pages/reporting/reports/rug/components/SectionAddendumsEditor'
import {
  RugReportSectionId,
  SectionAddendum,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React, { useMemo } from 'react'

export type SectionAddendumOption = {
  value: string
  label: string
  sectionId: string
}

export const getSectionAddendumFormValues = (
  sectionAddendums: SectionAddendum[],
  sectionOptions: SectionAddendumOption[]
) =>
  sectionAddendums.map((addendum) => {
    const existingSectionOption = sectionOptions.find(
      (option) => option.sectionId === addendum.sectionId
    )

    return {
      ...addendum,
      sectionId: existingSectionOption?.label ?? addendum.sectionId,
    }
  })

export const getSectionAddendumsForSave = (
  sectionAddendums: SectionAddendum[],
  sectionOptions: SectionAddendumOption[]
) =>
  sectionAddendums.map((addendum) => {
    const existingSectionOption = sectionOptions.find(
      (option) =>
        option.label === addendum.sectionId ||
        option.sectionId === addendum.sectionId
    )

    return {
      ...addendum,
      sectionId: existingSectionOption?.sectionId ?? addendum.sectionId,
    }
  })

const RenderReact: React.FC = () => {
  const {
    formData,
    report,
    metadataEntryId,
    handleSectionAddendumsFinish,
    reportSignatures,
    isDraftGenerating,
    isSavingAddendums,
  } = useRugReportOps()
  const sectionOptions = useMemo(
    () =>
      report.sections
        .filter(
          (section) => section.id !== RugReportSectionId.SECTION_ADDENDUMS
        )
        .map((section) => ({
          value: section.title,
          label: section.title,
          sectionId: section.id,
        })),
    [report.sections]
  )
  const initialAddendums = useMemo(
    () =>
      getSectionAddendumFormValues(formData.sectionAddendums, sectionOptions),
    [formData.sectionAddendums, sectionOptions]
  )
  const save = (addendums: SectionAddendum[]) =>
    handleSectionAddendumsFinish(
      getSectionAddendumsForSave(addendums, sectionOptions)
    )
  const readOnlyReason =
    formData.revisionMetadata.isPublished || formData.publishedPdfPath
      ? 'Published reports cannot be edited. Create a new revision to change addendums.'
      : formData.eSignatures?.records.length
        ? 'Void active signatures in Review before editing this report.'
        : reportSignatures.isBusy || isDraftGenerating || isSavingAddendums
          ? 'Wait for saving, signing, or PDF generation to finish before editing addendums.'
          : undefined

  return (
    <SectionAddendumsEditor
      key={metadataEntryId}
      initialAddendums={initialAddendums}
      sectionOptions={sectionOptions}
      metadataEntryId={metadataEntryId}
      readOnly={Boolean(readOnlyReason)}
      readOnlyReason={readOnlyReason}
      onSave={save}
    />
  )
}

export const SectionAddendumsSection: ReportSection = {
  id: RugReportSectionId.SECTION_ADDENDUMS,
  title: 'Section Addendums',
  description: 'Add images, PDFs, or blank pages to existing or new sections',
  required: false,
  RenderReact,
  RenderPDF: () => null,
  requiredFields: [],
  showInTOC: false,
}
